import { useEffect, useRef } from "react";
import { useLocation, useNavigate, useNavigationType, useSearchParams } from "react-router-dom";
import { Subtree } from "subtree.js/react";
import { pick } from "trunk.js";
import type { AppDeps } from "../../app-deps";
import { CardsController } from "./cards-controller";
import { CARD_FILTERS, CATALOGUE_KINDS, stockQuery } from "./cards-model";
import { CardsPage } from "./cards-page";

export function CardsRoute({ deps }: { deps: AppDeps }) {
  const navigate = useNavigate();
  const location = useLocation();
  const here = useRef(location);
  here.current = location;
  const pop = useNavigationType() === "POP";
  // The mark an open leaves on the Stock entry it leaves: the place kept for it. Back to that entry shows it.
  const mark = (location.state as { stockPlace?: unknown } | null)?.stockPlace;
  const marked = typeof mark === "string" ? mark : null;
  const [params] = useSearchParams();
  // The list's filter and search live in the URL, so Back from a card lands on the same list.
  const filter = CARD_FILTERS.find((f) => f.key === params.get("filter"))?.key ?? "all";
  const kind = CATALOGUE_KINDS.find((k) => k.key === params.get("kind"))?.key ?? "all";
  const search = params.get("q") ?? "";
  // Landed by Back: the mark comes off, so a later Back to this entry reads the list afresh.
  useEffect(() => {
    if (pop && marked !== null) navigate({ search: here.current.search }, { replace: true });
  }, [pop, marked, navigate]);
  return (
    <Subtree
      key="cards"
      controller={() =>
        new CardsController(
          pick(deps, "client", "authHandler", "storeRepository", "reviewQueueRepository", "stockListRepository"),
          {
            openCard: (cardId, section, place) => {
              navigate({ search: here.current.search }, { replace: true, state: { stockPlace: place } });
              navigate(`/cards/${cardId}`, section ? { state: { section } } : undefined);
            },
            showQuery: (shown, typed, kind) => navigate({ search: stockQuery(shown, typed, kind) }, { replace: true }),
          },
          { filter, kind, search, place: pop ? marked : null },
        )
      }
    >
      <CardsPage />
    </Subtree>
  );
}
